/** L9/L10 individual registration flow — Journalists & Talent/Models.
 *
 *  These accounts don't auto-verify. The individual picks ONE of three
 *  verification methods and sits in `verification_status: pending` until
 *  the chosen path resolves:
 *
 *    1. association  → picks an L1 trade association + membership number.
 *                      A trade-org admin approves from their pending queue.
 *    2. references   → nominates 3 already-verified peers of the same kind.
 *                      2 out of 3 vouches auto-approves.
 *    3. agency       → picks a sponsoring PR / Casting agency; the sponsor
 *                      org admin approves from `/pending-individuals`.
 *
 *  Shares the OnboardKit look-and-feel used by OnboardGeneric.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { setToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Stepper, Card, Field, NavRow } from "@/components/OnboardKit";
import OnboardBackButton from "@/components/OnboardBackButton";
import AffiliationsPicker from "@/components/AffiliationsPicker";
import { toast } from "sonner";
import { Mic2, UserSquare2, ShieldCheck, Search, Sparkles } from "lucide-react";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;
const STEPS = ["Personal", "Verification", "Submitted"];

const KIND_CFG = {
  journalist: {
    layer: "L10",
    label: "Journalist / Paparazzi / Influencers",
    testidPrefix: "onboard-journalist",
    accent: "#22D3EE",
    Icon: Mic2,
    tagline: "Reporters, film critics, digital creators, YouTubers and paparazzi.",
    sponsorLabel: "PR Agency",
    sponsorKind: "pr_agency",
    designationHint: "e.g. Film Critic · YouTuber · Paparazzo · Bureau Chief",
  },
  talent_model: {
    layer: "L12",
    label: "Talent / Model",
    testidPrefix: "onboard-talent-model",
    accent: "#A78BFA",
    Icon: UserSquare2,
    tagline: "Actors, models and performing artists.",
    sponsorLabel: "Casting Agency",
    sponsorKind: "casting_agency",
    designationHint: "e.g. Actor · Print Model · Runway · Voice Artist",
  },
};

const METHOD_OPTIONS = [
  { key: "association",
    label: "Via a Trade Association",
    hint:  "Attach your existing membership to an L1 association. The association admin approves you." },
  { key: "references",
    label: "Via 3 Peer References",
    hint:  "Nominate 3 already-verified peers of the same kind. When 2 vouch, you're in." },
  { key: "agency",
    label: "Via a Sponsoring Agency",
    hint:  "Pick your sponsoring PR or Casting agency. Their admin approves you." },
];

export default function OnboardIndividual({ kind }) {
  const cfg = KIND_CFG[kind];
  if (!cfg) throw new Error(`OnboardIndividual: unknown kind '${kind}'`);
  const { Icon } = cfg;

  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [associations, setAssociations] = useState([]);
  const [sponsorQuery, setSponsorQuery] = useState("");
  const [sponsorMatches, setSponsorMatches] = useState([]);
  const [sponsorPicked, setSponsorPicked] = useState(null);
  const [approved, setApproved] = useState(null);
  const [sectionAffiliations, setSectionAffiliations] = useState([]);
  // L9 Journalist multi-select of practice areas (print_reporter · digital_reporter
  // · film_critic · paparazzi · influencer · youtuber · podcaster). Ignored for
  // talent_model.
  const [journalistKindCatalog, setJournalistKindCatalog] = useState([]);
  const [journalistKinds, setJournalistKinds] = useState(new Set());
  useEffect(() => {
    if (kind !== "journalist") return;
    axios.get(`${API}/meta/journalist-kinds`)
      .then(({ data }) => setJournalistKindCatalog(Array.isArray(data) ? data : []))
      .catch(() => setJournalistKindCatalog([]));
  }, [kind]);
  const toggleJournalistKind = (key) => setJournalistKinds((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  // L12 Talent subtype (actor / model / variety_performer) + optional
  // multi-select variety skill tags. Only rendered when kind==="talent_model".
  const [talentSubtypeCatalog, setTalentSubtypeCatalog] = useState([]);
  const [talentSubtype, setTalentSubtype] = useState("actor");
  const [varietySkillCatalog, setVarietySkillCatalog] = useState([]);
  const [varietySkills, setVarietySkills] = useState(new Set());
  useEffect(() => {
    if (kind !== "talent_model") return;
    axios.get(`${API}/meta/talent-subtypes`)
      .then(({ data }) => setTalentSubtypeCatalog(Array.isArray(data) ? data : []))
      .catch(() => setTalentSubtypeCatalog([]));
    axios.get(`${API}/meta/variety-skills`)
      .then(({ data }) => setVarietySkillCatalog(Array.isArray(data) ? data : []))
      .catch(() => setVarietySkillCatalog([]));
  }, [kind]);
  const toggleVariety = (key) => setVarietySkills((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const [profile, setProfile] = useState({
    name: "", email: "", phone: "", password: "", designation: "",
    home_city: "",
  });
  const [method, setMethod] = useState("association");
  const [vfd, setVfd] = useState({
    association_id: "",
    membership_number: "",
    reference_emails: ["", "", ""],
    sponsor_org_id: "",
  });

  const upd = (setter) => (k, v) => setter((p) => ({ ...p, [k]: v }));
  const updProfile = upd(setProfile);
  const updVfd     = upd(setVfd);

  useEffect(() => {
    axios.get(`${API}/meta/associations`).then((r) => {
      setAssociations(Array.isArray(r.data) ? r.data : []);
    }).catch((e) => console.warn("Failed to load associations", e));
  }, []);

  // Sponsor org search (debounced).
  useEffect(() => {
    if (method !== "agency") return;
    const q = sponsorQuery.trim();
    if (q.length < 2) { setSponsorMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/orgs/search`, {
          params: { q, kind: cfg.sponsorKind, limit: 8 },
        });
        setSponsorMatches(data.results || []);
      } catch (e) {
        console.warn("Sponsor search failed", e);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [sponsorQuery, method, cfg.sponsorKind]);

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const canNextStep0 =
    profile.name.trim().length  >= 2 &&
    profile.email.includes("@")      &&
    profile.password.length     >= 6 &&
    sectionAffiliations.length  >= 1 &&
    (kind !== "journalist"   || journalistKinds.size >= 1) &&
    (kind !== "talent_model" || talentSubtype !== "variety_performer" || varietySkills.size >= 1);

  const canSubmit = (() => {
    if (!canNextStep0) return false;
    if (method === "association") return !!vfd.association_id;
    if (method === "references")
      return vfd.reference_emails.filter((e) => e.trim().includes("@")).length >= 3;
    if (method === "agency") return !!vfd.sponsor_org_id;
    return false;
  })();

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Please complete every required field before submitting.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        kind,
        name:               profile.name.trim(),
        email:              profile.email.trim().toLowerCase(),
        phone:              profile.phone.trim(),
        password:           profile.password,
        designation:        profile.designation.trim(),
        verification_method: method,
      };
      if (method === "association") {
        body.association_id     = vfd.association_id;
        body.membership_number  = vfd.membership_number.trim();
      } else if (method === "references") {
        body.reference_emails   = vfd.reference_emails
          .map((e) => e.trim().toLowerCase()).filter(Boolean);
      } else if (method === "agency") {
        body.sponsor_org_id     = vfd.sponsor_org_id;
      }
      if (sectionAffiliations.length) body.section_affiliations = sectionAffiliations;
      if (kind === "journalist" && journalistKinds.size) {
        body.journalist_kinds = Array.from(journalistKinds);
      }
      if (kind === "talent_model") {
        body.talent_subtype = talentSubtype;
        if (talentSubtype === "variety_performer" && varietySkills.size) {
          body.variety_skills = Array.from(varietySkills);
        }
      }
      // Directory city — permanent union base city + auto-list flag.
      if (profile.home_city.trim()) body.primary_association_city = profile.home_city.trim();
      const { data } = await axios.post(`${API}/individuals/pending-register`, body);
      await setToken(data.access_token);
      await refresh();
      setApproved(data.user);
      setStep(STEPS.length - 1);
      toast.success("Registration submitted — awaiting verification.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not register");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10" data-testid={cfg.testidPrefix}>
      <OnboardBackButton step={step} onStepBack={back} label="Back to Options" />

      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center"
             style={{ background: `${cfg.accent}22`, color: cfg.accent }}>
          <Icon size={22} />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            {cfg.layer} · Sign Up
          </div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight">{cfg.label}</h1>
        </div>
      </div>
      <p className="mt-3 text-sm text-white/60 max-w-xl">{cfg.tagline}</p>

      <Stepper steps={STEPS} step={step} />

      {step === 0 && (
        <Card title="Personal Details" testid={`${cfg.testidPrefix}-step-personal`}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name">
              <Input value={profile.name}
                onChange={(e) => updProfile("name", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                data-testid={`${cfg.testidPrefix}-name-input`} required />
            </Field>
            <Field label="Email">
              <Input type="email" value={profile.email}
                onChange={(e) => updProfile("email", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                data-testid={`${cfg.testidPrefix}-email-input`} required />
            </Field>
            <Field label="Phone">
              <Input value={profile.phone}
                onChange={(e) => updProfile("phone", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                data-testid={`${cfg.testidPrefix}-phone-input`} />
            </Field>
            <Field label="Password (min 6)">
              <Input type="password" value={profile.password}
                onChange={(e) => updProfile("password", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                data-testid={`${cfg.testidPrefix}-password-input`} required minLength={6} />
            </Field>
            <Field label="Designation / Beat">
              <Input value={profile.designation}
                onChange={(e) => updProfile("designation", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                placeholder={cfg.designationHint}
                data-testid={`${cfg.testidPrefix}-designation-input`} />
            </Field>
            <Field label="Home City (surfaces regional unions first)">
              <Input value={profile.home_city}
                onChange={(e) => updProfile("home_city", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                placeholder="e.g. Mumbai · Hyderabad · Chennai"
                data-testid={`${cfg.testidPrefix}-city-input`} />
            </Field>
          </div>
          <AffiliationsPicker
            section={kind}
            value={sectionAffiliations}
            onChange={setSectionAffiliations}
            city={profile.home_city}
            testidPrefix={`${cfg.testidPrefix}-aff`}
          />
          {kind === "talent_model" && (
            <>
              <div className="rounded-xl border border-white/12 bg-white/[0.02] p-4"
                   data-testid={`${cfg.testidPrefix}-subtype-picker`}>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
                  Talent Subtype *
                </div>
                <div className="mt-2 text-xs text-white/60 leading-relaxed">
                  Pick what best describes you. Variety performers unlock a skill picker below.
                </div>
                <div className="mt-3 grid sm:grid-cols-3 gap-2.5">
                  {talentSubtypeCatalog.map((t) => {
                    const on = talentSubtype === t.key;
                    return (
                      <button key={t.key} type="button"
                        onClick={() => setTalentSubtype(t.key)}
                        className={`p-3 rounded-lg text-left border transition-colors ${
                          on ? "border-white/45" : "bg-black/30 border-white/10 hover:bg-white/[0.05]"
                        }`}
                        style={on ? { borderColor: cfg.accent, background: `${cfg.accent}12` } : {}}
                        data-testid={`${cfg.testidPrefix}-subtype-${t.key}`}>
                        <div className="text-sm font-semibold text-white flex items-center gap-1.5">
                          {t.key === "variety_performer" && <Sparkles size={12} style={{ color: cfg.accent }}/>}
                          {t.label}
                        </div>
                        {t.blurb && <div className="mt-0.5 text-[11px] text-white/55">{t.blurb}</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
              {talentSubtype === "variety_performer" && (
                <div className="rounded-xl border border-white/12 bg-white/[0.02] p-4"
                     data-testid={`${cfg.testidPrefix}-variety-picker`}>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
                    Variety Skills *
                  </div>
                  <div className="mt-2 text-xs text-white/60 leading-relaxed">
                    Tick every skill you perform. Event producers filter by these tags.
                  </div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-2">
                    {varietySkillCatalog.map((t) => {
                      const on = varietySkills.has(t.key);
                      return (
                        <button key={t.key} type="button" onClick={() => toggleVariety(t.key)}
                          className={`p-2.5 rounded-lg text-left border transition-colors ${
                            on ? "border-white/45" : "bg-black/30 border-white/10 hover:bg-white/[0.05]"
                          }`}
                          style={on ? { borderColor: cfg.accent, background: `${cfg.accent}12` } : {}}
                          data-testid={`${cfg.testidPrefix}-variety-${t.key}`}>
                          <div className="text-[13px] font-semibold text-white">{t.label}</div>
                          {t.blurb && <div className="text-[10px] text-white/55 mt-0.5">{t.blurb}</div>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
          {kind === "journalist" && (
            <div className="rounded-xl border border-white/12 bg-white/[0.02] p-4"
                 data-testid={`${cfg.testidPrefix}-kind-picker`}>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
                Journalist Practice Areas *
              </div>
              <div className="mt-2 text-xs text-white/60 leading-relaxed">
                Pick every format you cover. Editors seeing your profile see this
                mix — multi-practice bylines are common.
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
                {journalistKindCatalog.map((t) => {
                  const on = journalistKinds.has(t.key);
                  return (
                    <button key={t.key} onClick={() => toggleJournalistKind(t.key)}
                      className={`p-3 rounded-lg text-left border transition-colors ${
                        on
                          ? "border-white/45"
                          : "bg-black/30 border-white/10 hover:bg-white/[0.05]"
                      }`}
                      style={on ? { borderColor: cfg.accent, background: `${cfg.accent}12` } : {}}
                      data-testid={`${cfg.testidPrefix}-kind-${t.key}`}>
                      <div className="text-sm font-semibold text-white">{t.label}</div>
                      {t.blurb && (
                        <div className="mt-0.5 text-[11px] text-white/55">{t.blurb}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <NavRow onNext={next} disabledNext={!canNextStep0} />
        </Card>
      )}

      {step === 1 && (
        <Card title="Verification Method" testid={`${cfg.testidPrefix}-step-verification`}>
          <p className="text-xs text-white/60 mb-4">
            {cfg.label}s aren&apos;t auto-verified. Pick the path that fits you.
          </p>
          <div className="grid gap-3">
            {METHOD_OPTIONS.map((m) => (
              <button key={m.key}
                type="button"
                onClick={() => setMethod(m.key)}
                data-testid={`${cfg.testidPrefix}-method-${m.key}`}
                className={`text-left rounded-xl border px-4 py-3 transition ${
                  method === m.key
                    ? "border-white/40 bg-white/[0.05]"
                    : "border-white/10 hover:border-white/25 bg-transparent"
                }`}>
                <div className="text-sm font-semibold uppercase tracking-[0.14em]"
                     style={{ color: method === m.key ? cfg.accent : "rgba(255,255,255,0.85)" }}>
                  {m.label}
                </div>
                <div className="text-xs text-white/60 mt-1 leading-relaxed">{m.hint}</div>
              </button>
            ))}
          </div>

          {method === "association" && (
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <Field label="Trade Association">
                <Select value={vfd.association_id}
                  onValueChange={(v) => updVfd("association_id", v)}>
                  <SelectTrigger className="bg-black/40 border-white/15 h-11"
                                 data-testid={`${cfg.testidPrefix}-association-select`}>
                    <SelectValue placeholder="Pick an association…" />
                  </SelectTrigger>
                  <SelectContent>
                    {associations.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Membership Number">
                <Input value={vfd.membership_number}
                  onChange={(e) => updVfd("membership_number", e.target.value)}
                  className="bg-black/40 border-white/15 h-11"
                  data-testid={`${cfg.testidPrefix}-membership-input`} />
              </Field>
            </div>
          )}

          {method === "references" && (
            <div className="mt-5 space-y-3">
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/55">
                Enter 3 verified {cfg.label.toLowerCase()} emails already on the platform
              </div>
              {vfd.reference_emails.map((val, idx) => (
                <Input key={`ref-${idx}`} type="email" value={val}
                  onChange={(e) => {
                    const arr = [...vfd.reference_emails];
                    arr[idx] = e.target.value;
                    updVfd("reference_emails", arr);
                  }}
                  placeholder={`Reference #${idx + 1} email`}
                  className="bg-black/40 border-white/15 h-11"
                  data-testid={`${cfg.testidPrefix}-reference-${idx}-input`} />
              ))}
              <p className="text-[11px] text-white/50 leading-relaxed">
                Each reference will see a vouch link in their Inbox. When 2 of 3
                vouch, you&apos;re auto-verified.
              </p>
            </div>
          )}

          {method === "agency" && (
            <div className="mt-5 space-y-3">
              <Field label={`Search for your sponsoring ${cfg.sponsorLabel}`}>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input value={sponsorQuery}
                    onChange={(e) => {
                      setSponsorQuery(e.target.value);
                      setSponsorPicked(null);
                      updVfd("sponsor_org_id", "");
                    }}
                    placeholder={`Type ${cfg.sponsorLabel} name…`}
                    className="bg-black/40 border-white/15 h-11 pl-9"
                    data-testid={`${cfg.testidPrefix}-sponsor-search`} />
                </div>
              </Field>
              {sponsorMatches.length > 0 && !sponsorPicked && (
                <div className="rounded-xl border border-white/10 divide-y divide-white/10 max-h-64 overflow-auto">
                  {sponsorMatches.map((s) => (
                    <button key={s.id}
                      type="button"
                      onClick={() => {
                        setSponsorPicked(s);
                        setSponsorQuery(s.brand_name);
                        updVfd("sponsor_org_id", s.id);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-white/[0.05]"
                      data-testid={`${cfg.testidPrefix}-sponsor-pick-${s.id}`}>
                      <div className="text-sm">{s.brand_name}</div>
                      <div className="text-[10px] text-white/50 uppercase tracking-[0.2em]">
                        {s.status || "verified"}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {sponsorPicked && (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200"
                     data-testid={`${cfg.testidPrefix}-sponsor-selected`}>
                  Sponsor: <span className="font-semibold">{sponsorPicked.brand_name}</span>
                </div>
              )}
            </div>
          )}

          <NavRow onBack={back} onSubmit={submit}
                  submitting={busy}
                  disabledNext={!canSubmit}
                  submitLabel={busy ? "Submitting…" : "Submit for Verification"} />
        </Card>
      )}

      {step === 2 && approved && (
        <Card title="Submitted · Pending Verification" testid={`${cfg.testidPrefix}-step-submitted`}>
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-display text-xl">Thanks, {approved.name}!</h3>
              <p className="text-sm text-white/70 mt-1.5">
                {method === "references"
                  ? "Your 3 references have been notified. You'll be verified as soon as 2 of them vouch."
                  : method === "agency"
                  ? "Your sponsoring agency has been notified. You'll be verified as soon as they approve."
                  : "Your association admin has been notified. You'll be verified as soon as they approve."}
              </p>
              <p className="text-[11px] text-white/45 mt-2">
                You can log in and edit your profile, but any verified-only
                actions stay disabled until approval lands.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => nav("/dashboard")}
              className="btn-themed rounded-full h-11 px-6 text-xs uppercase tracking-[0.22em] font-semibold"
              data-testid={`${cfg.testidPrefix}-go-dashboard`}>
              Go to Dashboard →
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}
